import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import { FieldContent, FieldError, FieldLabel } from '~/shadcn/ui/field'
import { Checkbox } from '~/shadcn/ui/form/checkbox'
import { Input } from '~/shadcn/ui/input'
import { Spinner } from '~/shadcn/ui/spinner'
import {
  addBankAccountSchema,
  type AddBankAccountSchema,
} from './addBankAccountSchema'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { Button } from '~/shadcn/ui/button/button'

export type AddBankAccountFormData = AddBankAccountSchema

type Props = {
  isRequesting: boolean
  error?: string
  onSubmit: (formData: AddBankAccountFormData) => void
}

function AddBankAccountForm({ isRequesting, onSubmit, error }: Props) {
  const form = useForm<AddBankAccountSchema>({
    defaultValues: { iban: '', isOwner: false },
    resolver: zodResolver(addBankAccountSchema),
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-8">
          <FormField
            name="iban"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem className="w-full">
                <FieldLabel>Váš IBAN</FieldLabel>
                <FieldContent>
                  <Input
                    aria-invalid={!!fieldState.error}
                    autoComplete="iban"
                    autoFocus
                    className="font-semibold"
                    {...field}
                  />
                  {fieldState.error && (
                    <p className="text-sm text-destructive font-semibold">
                      {fieldState.error.message}
                    </p>
                  )}
                </FieldContent>
              </FormItem>
            )}
          />

          <FormField
            name="isOwner"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem className="w-full ">
                <FieldContent>
                  <FieldLabel
                    htmlFor="isOwner"
                    className="flex flex-row items-center gap-2 font-semibold cursor-pointer">
                    <Checkbox
                      id="isOwner"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && field.onChange(!field.value)
                      }
                    />
                    Uvedený bankový účet je môj
                  </FieldLabel>
                </FieldContent>
                {fieldState.error && (
                  <FieldError className="text-sm text-destructive">
                    {fieldState.error.message}
                  </FieldError>
                )}
              </FormItem>
            )}
          />

          <Button
            variant="accent"
            type="submit"
            className="lg:w-full"
            disabled={form.formState.isSubmitting || isRequesting}>
            Pridať účet{` `}
            {isRequesting && <Spinner className="size-4" />}
          </Button>

          {error && <FormError error={error} />}
        </div>
      </form>
    </Form>
  )
}

export { AddBankAccountForm }
